import Loading from '@/components/Loading'
import NavBar from '@/components/NavBar'
import ProductListCard from '@/components/PCP/ProductListCard'
import SearchRecommendations from '@/components/Search/SearchRecommendations'
import AppContainer from '@/layouts/AppContainer'
import { shopifyGraphqlClient } from '@/lib/shopifyGraphql'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import React, { useCallback } from 'react'
import ReactVisibilitySensor from 'react-visibility-sensor'
import { GetProductsDocument, GetProductsQuery, PageInfo, Product } from '../../shopify/api'
import Search404 from '../Search404'
import { WifNotification } from '../WifNotification'

interface SearchPageContentsProps {
  from: string
  query: (searchQuery: string) => string
  searchRecommendationsQuery: string
  searchPlaceholder: string
  searchPageHref: string
  searchRecommendationsPDPPrefix?: string
}

const SearchPageContents: React.FC<SearchPageContentsProps> = ({
  from,
  query,
  searchRecommendationsQuery,
  searchPlaceholder,
  searchPageHref,
  searchRecommendationsPDPPrefix,
}) => {
  const [isInitialLoad, setIsInitialLoad] = React.useState(true)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<Product[]>([])
  const [pageInfo, setPageInfo] = React.useState<PageInfo>({
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null,
  })
  const [isLoading, setIsLoading] = React.useState(false)

  const { t } = useTranslation('pcp')

  const handleSearch = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return

      setIsLoading(true)
      const { data } = await shopifyGraphqlClient.query<GetProductsQuery>({
        query: GetProductsDocument,
        variables: {
          query: query(searchQuery),
        },
      })

      const { pageInfo, nodes } = data?.products

      setIsLoading(false)
      setSearchResults(nodes as Product[])
      setPageInfo(pageInfo)
      setIsInitialLoad(false)
    },
    [searchQuery]
  )

  const onLoadMore = async (isVisible: boolean, endCursor: String) => {
    if (!isVisible) return
    const { data } = await shopifyGraphqlClient.query<GetProductsQuery>({
      query: GetProductsDocument,
      variables: {
        query: `title:${searchQuery} OR tag:${searchQuery}`,
        after: endCursor,
      },
    })

    const { pageInfo, nodes } = data?.products

    setIsLoading(false)
    setSearchResults([...searchResults, ...(nodes as Product[])])
    setPageInfo(pageInfo)
  }

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (searchQuery === e.target.value) return
      setSearchQuery(e.target.value)
    },
    [searchQuery]
  )

  const handleQueryClear = useCallback(() => {
    setSearchQuery('')
  }, [searchQuery])

  const render = () => {
    if (isInitialLoad)
      return (
        <SearchRecommendations
          query={searchRecommendationsQuery}
          pdpPrefix={searchRecommendationsPDPPrefix}
        />
      )
    if (isLoading) return <Loading />
    if (searchResults.length <= 0)
      return (
        <Search404 explorePageHref={explorePageHrefMap(searchRecommendationsPDPPrefix || '')} />
      )

    return (
      <>
        <ProductListCard id='search-results' products={searchResults} />
        {pageInfo?.hasNextPage && (
          <ReactVisibilitySensor
            onChange={(isVisible: boolean) => onLoadMore(isVisible, pageInfo.endCursor!)}
          >
            <div className='text-center'>{t('loading_more')}</div>
          </ReactVisibilitySensor>
        )}
      </>
    )
  }

  const explorePageHrefMap = (prefix: string) => {
    switch (prefix) {
      case 'made-to-order':
        return '/made-to-order'
      default:
        return from
    }
  }

  return (
    <AppContainer>
      <Head>
        <title>Search - Wifkain</title>
      </Head>
      <NavBar
        simplified
        rightActions={[]}
        search={{
          handleSearch,
          searchQuery,
          handleQueryChange,
          handleQueryClear,
        }}
        searchPageHref={searchPageHref}
        searchPlaceholder={searchPlaceholder}
        from={from}
        backButton
      />
      <div className='py-[70px] min-h-screen bg-white'>
        <div>{render()}</div>
      </div>
      <WifNotification />
    </AppContainer>
  )
}

export default SearchPageContents
